// Libraries
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, effect, HostListener, inject, OnDestroy, OnInit, Signal, signal, ViewChild, ViewEncapsulation, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { MapThemeDirective } from 'src/app/directives/map-theme.directive';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatToolbarModule } from '@angular/material/toolbar';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { differenceInMilliseconds } from 'date-fns';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, finalize, take, takeUntil } from 'rxjs/operators';

// Services
import { AttachmentService } from 'src/app/services/api/attachments/attachment.service';
import { ModelService } from 'src/app/services/api/models/model.service';
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { TrackService } from 'src/app/services/api/tracks/track.service';
import { UnitService } from 'src/app/services/api/units/unit.service';
import { UserService } from 'src/app/services/api/users/user.service';
import { ZoneService } from 'src/app/services/api/zones/zone.service';
import { AuthService } from 'src/app/services/core/auth.service';

// Interfaces
import { Attachment } from 'src/app/interfaces/api/attachment';
import { ImageMask, Model } from 'src/app/interfaces/api/model';
import { Project, PutProject } from 'src/app/interfaces/api/project';
import { defaultTrack, Track } from 'src/app/interfaces/api/track';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { Unit } from 'src/app/interfaces/api/unit';
import { User } from 'src/app/interfaces/api/user';
import { PostZone, Zone } from 'src/app/interfaces/api/zone';
import { Position } from 'src/app/interfaces/shared/position';

// Classes
import { ImageTransform } from 'src/app/classes/maps/ImageTransform';
import { MapCollider } from 'src/app/classes/maps/MapCollider';
import { ZoneCreate } from 'src/app/classes/maps/ZoneCreate';
import { Point, ZoneTransform } from 'src/app/classes/maps/ZoneTransform';
import { Pop } from 'src/app/classes/shared/pop';
import { Queue } from 'src/app/classes/shared/queue';
import * as Tool3D from 'src/app/classes/shared/threeDTools';
import { normalizeModel, Tools } from 'src/app/classes/shared/tools';
import { UiTool } from 'src/app/classes/shared/ui.tools';


// Components
import { ContextMenuComponent } from 'projects/ui-library/src/lib/components/context-menu/context-menu.component';
import { ContextMenuItem } from 'projects/ui-library/src/lib/interfaces/context-menu/contextMenuItem';
import { ImageMaskEditor } from 'src/app/classes/maps/ImageMaskEditor';
import { TrackerTool } from 'src/app/classes/shared/tracker.tools';
import { ConfirmMapComponent } from 'src/app/components/project-pages/viewer-maps/confirm-map/confirm-map.component';
import { CoordinateComponent } from 'src/app/components/project-pages/viewer-maps/coordinate/coordinate.component';
import { OriginDialogComponent } from 'src/app/components/project-pages/viewer-maps/dialogs/origin/origin-dialog.component';
import { OverlayDialogComponent } from 'src/app/components/project-pages/viewer-maps/dialogs/overlay/overlay-dialog.component';
import { TracesDialogComponent } from 'src/app/components/project-pages/viewer-maps/dialogs/traces/traces-dialog.component';
import { ZonesDialogComponent } from 'src/app/components/project-pages/viewer-maps/dialogs/zones/zones-dialog.component';
import { ALL_ROLES, TagDisplayConfig } from 'src/app/components/shared/gallery/gallery.component';
import { PlayerComponent } from 'src/app/components/shared/player/player.component';
import { TrackerPositionComponent } from 'src/app/components/shared/tracker-position/tracker-position.component';
import { MapStore } from 'src/app/services/stores/map.store';
import { ButtonComponent, ConfirmComponent, DatePickerComponent } from 'ui-library';

@Component({
  selector: 'app-viewer-maps',
  templateUrl: './viewer-maps.component.html',
  styleUrl: './viewer-maps.component.scss',
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatCardModule,
    MatButtonToggleModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatMenuTrigger,
    MatMenuModule,
    MatDatepickerModule,
    DragDropModule,
    ReactiveFormsModule,
    FormsModule,
    GoogleMapsModule,
    MapThemeDirective,
    TranslateModule,
    ConfirmMapComponent,
    PlayerComponent,
    ContextMenuComponent,
    TrackerPositionComponent,
    CoordinateComponent,
    ButtonComponent,
    DatePickerComponent
  ]
})

export class ViewerMapsComponent implements OnInit, AfterViewInit, OnDestroy {
  // Services
  private readonly authService: AuthService = inject(AuthService);
  private readonly modelService: ModelService = inject(ModelService);
  private readonly zoneService: ZoneService = inject(ZoneService);
  private readonly unitService: UnitService = inject(UnitService);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly userService: UserService = inject(UserService);
  private readonly trackerService: TrackerService = inject(TrackerService);
  private readonly trackService: TrackService = inject(TrackService);
  private readonly attachmentService: AttachmentService = inject(AttachmentService);
  private readonly projectService: ProjectService = inject(ProjectService);
  public readonly pop: Pop = inject(Pop);
  public readonly tools: Tools = inject(Tools);
  public readonly trackerTools: TrackerTool = inject(TrackerTool);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly fb: FormBuilder = inject(FormBuilder);
  private readonly translate: TranslateService = inject(TranslateService);
  public readonly uiTool: UiTool = inject(UiTool);
  private readonly router: Router = inject(Router);
  private readonly mapStore: MapStore = inject(MapStore);

  // Components API
  @ViewChild(GoogleMap) map!: GoogleMap;

  // Signals
  readonly user: Signal<User> = this.authService.currentUser;
  readonly project: Signal<Project> = this.projectService.currentProjectSignal;
  readonly isMobileSignal: Signal<boolean> = this.uiTool.isMobileSignal;
  readonly activitySignal: WritableSignal<any> = signal(null);
  public readonly zoneColors: WritableSignal<{ title: string; value: string }[]> = signal([
    { title: 'Danger', value: 'danger' },
    { title: 'Warning', value: 'warning' },
    { title: 'Primary', value: 'primary' },
    { title: 'Success', value: 'success' },
    { title: 'Info', value: 'info' },
    { title: 'Dark', value: 'dark' },
    { title: 'Muted', value: 'muted' },
  ]);

  currentZone: WritableSignal<Zone | null> = signal<Zone | null>(null);
  editedZone: WritableSignal<Zone | null> = signal<Zone | null>(null);
  currentImage: WritableSignal<Model | null> = signal<Model | null>(null);
  editedImage: WritableSignal<Model | null> = signal<Model | null>(null);
  transformedImage: WritableSignal<Model | null> = signal<Model | null>(null);

  // Variables
  title: string = 'google-maps';
  mapObject: any;
  projectXY = [0, 0];
  mapAngle: number = 0;
  userLastPosition: { lat: number; lon: number; zoom: number; tilt: number; projectId?: string }[] = [];
  viewerPositions: any[] = [];
  projectId: string | null = null;
  mapOptions: google.maps.MapOptions = {};
  markerPosition: google.maps.LatLngLiteral | null = null;
  showMap = true;
  polygonZones: google.maps.Polygon[] = [];
  zones: Zone[] = [];
  minUnitScale = 1;
  colors: Record<string, string> = {
    worker: '#0066FF',
    crane: '#FFA500',
    equipment: '#FF3300',
    event: '#EEBB39',
    warning: '#FF6600',
    danger: '#DD4B39',
    primary: '#3C8DBC',
    success: '#00A65A',
    lemon: '#F3E612',
    teal: '#39CCCC',
    fuchsia: '#F012BE',
    dark: '#010101',
    info: '#666666',
    mute: '#808080',
    white: '#FFFFFF',
    black: '#000000',
    default: '#99BBAA'
  };
  units: Unit[] = [];
  markers: Record<string, any> = {};
  // Trackers of type "Tablet" (see getTrackerType() in unifield-api-v3) are the phone/tablet
  // connection heartbeat (app.component.ts's setTracks()) - they're never attached to a Unit,
  // so they're rendered as standalone markers here instead of going through the
  // Unit/collider-driven initTrackers()/updateMapTrack() pipeline above.
  tabletMarkers: Record<string, google.maps.Marker> = {};
  tabletTrackerIds: Set<string> = new Set();
  altitude = 0;
  trackerObservableArray: any[] = [];
  createZone = false;
  modelData: Model | null = null;
  groundOverlay: google.maps.GroundOverlay | null = null;
  processing = false;
  queue: Queue = new Queue();
  statusColor = 'success';
  loadedTraces: any[] = [];
  loadedMarkers: any[] = [];
  trackers: Tracker[] = [];
  pAttachments: Attachment[] = [];
  lastAttachment: Attachment | null = null;
  lastPredictions: any[] = [];
  showLastAttachment = false;
  predictionTrackers: Tracker[] = [];
  sAttachments: Attachment[] = [];
  imageHandler!: ImageTransform;
  playMode: string = 'realtime';
  mergingPoint$: BehaviorSubject<any> = new BehaviorSubject(false);
  startDate!: string;
  endDate!: string;
  zoneEditor!: ZoneTransform | null;
  icons: Record<string, string> = {};
  trackerConfigs: any[] = [];
  isInitialized = false;
  showTimeline = true;
  liveState: boolean | string = "live";
  zonesVisibles: Record<string, boolean> = {};
  fastReplayTimeouts: any[] = [];
  hasCalledOnPlayerClicked = false;
  clickedMarkerInfo: any;
  showFloatingWindow!: boolean;
  trackInterval: any;
  virtualUnitsInfo: any[] = [];
  hasStartedSendingTracks: boolean = false;
  dblClickListener: google.maps.MapsEventListener | null = null;
  intervalId: any;
  intervalIds: Record<string, any> = {};
  virtualUnits: any[] = [];
  replayUnitNames: string[] = [];
  centered: boolean = false;
  replayUnits: any[] = [];
  replayTrackers: string[] = [];
  zonePoints: any[] = [];
  editingZone: boolean = false;
  editingImage: boolean = false;
  titleImage: string = 'viewer.editImage';
  imageAngle: number = 0;
  ratios: { width: number; height: number } | null = null;
  resizedCoords: { x: number; y: number } | null = null;
  resizeTimeout: any;
  debugMarkers: any[] = [];
  destroy$ = new Subject<void>();
  canvasElement: HTMLCanvasElement | null = null;
  lastMouseEvent: MouseEvent | null = null;
  ctrlKeyDown: boolean = false;
  zKeyDown: boolean = false;
  yKeyDown: boolean = false;
  mergedPoint: any;
  showTrackerPosition: boolean = false;
  zoneCreator!: ZoneCreate | null;
  mouseDownTime: Date | null = null;
  duplicatingZone: boolean = false;
  geolocationMarker: google.maps.Marker | null = null;
  showContextMenu: boolean = false;
  menuPosition!: { x: number, y: number };
  editingPoint?: Point | null;
  mapRect!: DOMRect;
  menuItems: ContextMenuItem[] = [
    {
      label: 'form.delete',
      icon: 'delete_forever',
      iconColor: "rgba(183, 3, 3, 0.9)",
      action: () => this.deletePoint(),
      disabled: () => !this.zoneEditor || this.zoneEditor.points.length <= 3
    },
    {
      label: 'form.update',
      icon: 'edit',
      iconColor: 'blue',
      submenu: [
        { label: 'units.tracker_pos.from_tracker', icon: 'my_location', iconColor: 'rgba(70, 143, 60, 0.9)', action: () => this.openTrackerPosition() },
        { label: 'units.unit_pos.from_unit', icon: 'location_on', iconColor: 'rgba(70, 143, 60, 0.9)', action: () => this.useUnitPosition = true }
      ]
    }
  ];
  useUnitPosition: boolean = false;
  unitPoints: any[] = [];
  tagDisplayConfig: TagDisplayConfig = {
    visibility: {
      crane: { ai: ALL_ROLES, manual: ALL_ROLES },
      worker: { ai: ALL_ROLES, manual: ALL_ROLES },
      action: { ai: ALL_ROLES, manual: ALL_ROLES },
      zone: { ai: ALL_ROLES, manual: ALL_ROLES },
      risk: { ai: ['dev'], manual: ['dev', 'admin'] }
    }
  };
  private mapReady$ = new BehaviorSubject<boolean>(false);

  zoneForm!: FormGroup;
  currentZonePoint?: Point;
  mapOnly = false;
  private routerEventsBound: boolean = false;
  private isViewerMapsActive: boolean = true;
  currentImageOpacity: number = 100;
  private lastImageActionSeq = 0;

  // Computeds
  @HostListener("keydown", ['$event'])
  onKeydownEvent(event: KeyboardEvent) {
    const allowedKeyCodes = [17, 90, 89];
    if (!allowedKeyCodes.includes(event.keyCode)) {
      return;
    }

    // If ctrl key down
    if (!this.ctrlKeyDown && event.keyCode == 17 && (this.zoneEditor || this.zoneCreator)) {
      this.ctrlKeyDown = true;
      this.hoverNearestPoint();
    }

    // if z key down
    if (this.ctrlKeyDown && !this.zKeyDown && event.keyCode == 90 && (this.zoneEditor || this.zoneCreator)) {
      this.zKeyDown = true;
      if (this.zoneEditor) {
        this.zoneEditor.cancelAction();
      }
      if (this.zoneCreator) {
        this.zoneCreator.cancelAction();
      }
    }

    // if y key down
    if (this.ctrlKeyDown && !this.yKeyDown && event.keyCode == 89 && (this.zoneEditor || this.zoneCreator)) {
      this.yKeyDown = true;
      if (this.zoneEditor) {
        this.zoneEditor.redoAction();
      } else if (this.zoneCreator)
        this.zoneCreator.redoAction();
    }
  }

  @HostListener("keyup", ['$event'])
  onKeyupEvent(event: KeyboardEvent) {
    const allowedKeyCodes = [17, 90, 89];
    if (!allowedKeyCodes.includes(event.keyCode)) {
      return;
    }

    // If ctrl key up
    if (this.ctrlKeyDown && event.keyCode == 17 && (this.zoneEditor || this.zoneCreator)) {
      this.ctrlKeyDown = false;
      let zonePoints = this.zonePoints;

      if (this.zoneEditor) {
        let editedZoneId = this.editedZone()?.id
        zonePoints = this.zonePoints.filter(z => z.zoneId != editedZoneId)
      }

      zonePoints.filter(z => z.visible).forEach(p => {
        p.marker.setVisible(false);
        p.hoverMarker.setVisible(false);
      });

      this.unitPoints.forEach(u => {
        if (u.marker && typeof u.marker.getLabel === 'function') {
          const currentLabel = u.marker.getLabel();
          u.marker.setLabel({ ...currentLabel, className: "marker-label" });
        }
      });

      this.mergingPoint$.next(false);
    }

    if (this.zKeyDown) {
      this.zKeyDown = false;
    }

    if (this.yKeyDown)
      this.yKeyDown = false;
  }

  @HostListener("click", ['$event'])
  onMouseclick(event: MouseEvent) {
    if (this.zoneEditor) {
      const target = event.target as HTMLElement;

      if (target && (target.closest('.context-menu') || target.closest('.floating-window') || this.useUnitPosition || this.showTrackerPosition)) {
        return;
      }
      this.showContextMenu = false;
      this.zoneEditor.blurPoint();
      // this.editingPoint = null;
    }
  }

  @HostListener("mousedown", ['$event'])
  onMousedown(event: MouseEvent) {
    if (this.zoneCreator) {
      this.mouseDownTime = new Date();
    }
  }

  @HostListener("mouseup", ['$event'])
  onMouseup(event: MouseEvent) {
    if (this.zoneCreator && this.mouseDownTime && differenceInMilliseconds(new Date(), this.mouseDownTime) < 200) {
      const target = event.target as HTMLElement;

      if (target && (target.closest('button') || target.closest('.floating-window'))) {
        return;
      }

      const mouseEvent = this.lastMouseEvent;
      let mergingPoint = this.mergingPoint$.value;

      if (mergingPoint) {
        this.zoneCreator.addPoint(mergingPoint.lat, mergingPoint.lon);
      } else if (mouseEvent) {
        const latLon = this.getMapCoords(mouseEvent.clientX, mouseEvent.clientY);
        this.zoneCreator.addPoint(latLon.lat, latLon.lon);
      }
    }
  }

  @HostListener('mousemove', ['$event'])
  onMouseMove(event: MouseEvent) {
    this.lastMouseEvent = event;
    if (this.ctrlKeyDown) {
      this.hoverNearestPoint();
    }
  }

  constructor() {
    this.bindRouterVisibilityEvents();

    this.zoneForm = this.fb.group({
      name: ['',],
      color: ['',],
      opacity: ['',],
      startDate: [null,],
      endDate: [null,],
    });

    this.zoneForm.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.currentZone.update(zone => ({
        ...zone,
        ...value
      }));
    });

    this.replayUnitNames = this.route.snapshot.queryParamMap.get('replayUnits') ? this.route.snapshot.queryParamMap.get('replayUnits')!.split(", ") : [];
    this.mapOnly = this.route.snapshot.queryParamMap.get('mapOnly') == 'true' ? true : false;

    // 🎯 Effect: Change project
    effect(() => {
      const project = this.project();

      if (!project?.id) {
        this.cleanup();
        this.projectId = null;
        return;
      }

      if (this.projectId && this.projectId !== project.id) {
        this.cleanup();
        this.resetVariables();
        this.projectId = project.id;
        this.ngOnInit();
      } else if (!this.projectId) {
        this.projectId = project.id;
      }

      if (!this.modelData || this.projectId !== project.id) {
        const model = project.model;
        const modelId = typeof model === 'string' ? model : (model && typeof model === 'object' && 'id' in model ? model.id : undefined);

        if (modelId) {
          this.getModel(modelId);
        }
      }

      this.projectId = this.project().id;

      this.projectService.getActivity({ databases: [project.database] }).pipe(takeUntil(this.destroy$)).subscribe((activity: any) => {
        this.activitySignal.set(activity);
      });
    })

    if (this.user()) {
      this.viewerPositions = this.user().viewerPositions;
    }

    // 🚧 Effect : Change current zone
    effect(() => {
      let zone = this.currentZone();
      this.zoneForm.patchValue({ ...zone }, { emitEvent: false });
    });

    // 🛠️ Effect : Change editor mode
    effect(() => {
      const mode = this.mapStore.editorMode();

      if (mode !== 'transform') {
        if (this.imageHandler) {
          this.imageHandler.destroy();
        }

        return;
      }

      if (!this.modelData) return;

      (async () => {
        if (this.modelData?.mapCoord) {
          await this.ensureMapImageCanvas();

          this.editingImage = true;

          const editableImage = { ...this.modelData };
          this.currentImage.set({ ...this.modelData });
          this.editedImage.set(editableImage);

          this.currentImageOpacity = Math.round(this.getOpacity(editableImage) * 100);

          this.applyImageTransform(this.modelData);
          let imageData: any = this.transformedImage();

          this.imageHandler = new ImageTransform({
            map: this.map,
            imageCoord: this.modelData.mapCoord,
            imageData,
            onUpdate: (newCoords, resized = false) => {
              const current = this.editedImage();
              if (!current) return;

              const updatedImage = {
                ...current,
                mapCoord: newCoords,
                mapCenter: {
                  lat: (newCoords.north + newCoords.south) / 2,
                  lon: (newCoords.east + newCoords.west) / 2
                }
              };

              this.editedImage.set(updatedImage);

              if (this.map) {
                this.updateMap(updatedImage);
              }
            }
          });

        } else if (this.imageHandler) {
          this.imageHandler.enable();
        }
      })();
    });

    // 🛠️ Effect : Change image action
    effect(() => {
      const action = this.mapStore.imageAction();
      if (!action || action.seq === this.lastImageActionSeq) return;

      this.lastImageActionSeq = action.seq;

      if (action.type === 'delete') {
        if (this.imageHandler) {
          this.imageHandler.deleteImage();
        }

        if (this.groundOverlay) {
          this.groundOverlay.setMap(null);
          this.groundOverlay = null;
        }

        return;
      }

      if (action.type === 'upload') {
        const updated = this.mapStore.currentModel();

        if (this.map && updated) {
          this.modelData = normalizeModel(updated);
          this.loadMap(this.modelData);
        }
      }
    });
  }

  /////////////////////////////
  // Angular lifecycle hooks //
  /////////////////////////////

  ngOnInit(): void {
    this.getTrackers();
    this.getListAttachments();
    this.getConfigs();
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.map) {
        this.mapReady$.next(true);
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.mapReady$.complete();

    Object.keys(this.intervalIds).forEach((name) => {
      clearInterval(this.intervalIds[name]);
    });

    if (this.trackInterval) {
      clearInterval(this.trackInterval);
      this.trackInterval = null;
    }
  }

  timeout(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  getConfigs() {
    this.trackerService.getConfigs().pipe(takeUntil(this.destroy$)).subscribe(res => {
      if (res) {
        this.trackerConfigs = res;
      }
    })
  }

  cleanup(): void {
    Object.keys(this.intervalIds).forEach((name) => {
      clearInterval(this.intervalIds[name]);
    });

    if (this.loadedMarkers.length) {
      this.loadedMarkers.forEach(m => {
        if (m.marker) m.marker.setMap(null);
      });

      this.loadedMarkers = [];
    }

    if (this.loadedTraces.length) {
      this.loadedTraces.forEach(t => {
        if (t.marker) t.marker.setMap(null);
      });

      this.loadedTraces = [];
    }

    if (this.zones && this.zones.length > 0) {
      this.zones.forEach(zone => {
        if (zone.polygonObj) {
          zone.polygonObj.setMap(null);
        }
      });

      this.zones = [];
    }

    if (this.markers) {
      Object.keys(this.markers).forEach(k => {
        if (this.markers[k].marker) this.markers[k].marker.setMap(null);
      });

      this.markers = {};
    }

    if (this.zonePoints.length) {
      this.zonePoints.forEach(p => {
        if (p.marker) ['marker', 'duplicateMarker', 'hoverMarker'].forEach(key => p[key]?.setMap(null));
      });

      this.zonePoints = [];
    }

    if (this.polygonZones.length) {
      this.polygonZones.forEach(p => {
        if (p) p.setMap(null);
      });

      this.polygonZones = [];
    }

    if (this.geolocationMarker) {
      this.geolocationMarker.setMap(null);
      this.geolocationMarker = null;
    }

    Object.keys(this.tabletMarkers).forEach(k => this.tabletMarkers[k]?.setMap(null));
    this.tabletMarkers = {};
    this.tabletTrackerIds = new Set();
  }

  getModel(modelId: string): void {
    this.modelService.getModel(modelId).pipe(takeUntil(this.destroy$)).subscribe({
      next: async (results: Model) => {
        try {
          if (!results) {
            console.error("Model not found");
            this.cleanup();
            return;
          }

          const normalized = normalizeModel(results);
          const object = {
            mapCoord: normalized.mapCoord ?? {},
            mapCenter: normalized.mapCenter ?? {},
            origin: normalized.origin ?? {},
            mapImage: normalized.mapImage ?? "",
            opacity: this.getOpacity(normalized)
          };

          this.modelData = normalized;

          if (this.modelData && this.modelData.mapImageSrc && !this.modelData.mapImage) {
            this.modelData.mapImage = this.modelData.mapImageSrc;
          }

          this.mapAngle = results.origin?.mapAngle ?? 0;

          if (this.modelData && this.modelData.origin && !this.modelData.origin.mapAngle) {
            this.modelData.origin.mapAngle = 0;
          }

          this.userLastPosition = this.viewerPositions.filter(t => t.projectId == this.projectId) as { lat: number; lon: number; zoom: number; tilt: number; projectId?: string }[];
          this.mapObject = object;

          if (object.origin.position?.lat && object.origin.position?.lon) {
            this.projectXY = Tool3D.getXYFromLatLonAlt2(object.origin.position.lat, object.origin.position.lon, 0);
          }

          this.mapReady$.pipe(
            filter(ready => ready === true),
            take(1),
            takeUntil(this.destroy$)
          ).subscribe(() => {
            this.loadMap(this.mapObject);
          });
        } catch (e) {
          console.error("Error: ", e);
        }
      },
      error: (error: any) => {
        this.showMap = false;
        console.error(error);
      }
    });
  }

  updateMap(object: Model) {
    if (this.groundOverlay) {
      this.groundOverlay.setMap(null);
    }

    const imageUrl = object.mapImage ?? null;
    const opacity = this.getOpacity(object)
    const bounds = this.toLatLngBounds(object.mapCoord);

    if (imageUrl && !bounds) {
      console.warn("Invalid mapCoord, ground overlay skipped:", object.mapCoord);
    }

    if (imageUrl && bounds) {
      this.groundOverlay = new google.maps.GroundOverlay(
        imageUrl,
        bounds,
        {
          clickable: false,
          opacity
        }
      );

      if (this.map?.googleMap) {
        this.groundOverlay.setMap(this.map.googleMap);
      }
    }
  }

  // Google Maps rejects any bound that is not a finite number (missing, null or string values)
  private toLatLngBounds(coord: Model['mapCoord'] | null | undefined): google.maps.LatLngBoundsLiteral | null {
    if (!coord) return null;

    const raw = [coord.north, coord.south, coord.east, coord.west] as unknown[];
    if (raw.some(v => v === null || v === undefined || v === '')) return null;

    const [north, south, east, west] = raw.map(Number) as [number, number, number, number];
    if (![north, south, east, west].every(Number.isFinite)) return null;

    return { north, south, east, west };
  }

  setDebugMarker(title: string, lat: number, lon: number) {
    let url = "/img/sigle-noshadow.png";

    let max = 48;
    if (this.debugMarkers.length > max) {
      this.debugMarkers.forEach((m: any, i: number) => {
        if (i < max)
          m.setMap(null);
      });

      this.debugMarkers = this.debugMarkers.slice(max);
    }

    this.debugMarkers.push(new google.maps.Marker({
      title,
      label: {
        text: title,
        color: 'black',
        className: `marker-label active`
      },
      position: new google.maps.LatLng(lat, lon),
      icon: url,
      map: this.map.googleMap,
      opacity: 0.8,
      zIndex: 10
    }))
  }

  loadMap(object: any): void {
    let mapOptions: google.maps.MapOptions = {
      center: this.userLastPosition.length > 0 ? { lat: this.userLastPosition[0]?.lat, lng: this.userLastPosition[0]?.lon } : { lat: object.mapCenter?.lat || 0, lng: object.mapCenter?.lon || 0 },
      zoom: this.mapOptions?.zoom || (this.userLastPosition.length != 0 ? this.userLastPosition[0]?.zoom : 15),
      tilt: this.userLastPosition.length != 0 ? this.userLastPosition[0]?.tilt : 0,
      mapTypeId: google.maps.MapTypeId.SATELLITE
    }

    this.mapOptions = mapOptions;

    if (this.map?.googleMap && object.origin?.position?.lat && object.origin?.position?.lon) {
      new google.maps.Marker({
        position: { lat: object.origin.position.lat, lng: object.origin.position.lon },
        visible: false,
        map: this.map.googleMap,
      });
    }

    if (!this.modelData) return;

    this.applyImageTransform(this.modelData);

    if (this.map) {
      this.map.mapDblclick.pipe(takeUntil(this.destroy$)).subscribe((data) => {

        const streetView = this.map.getStreetView();
        if (data.latLng) {
          streetView.setOptions({
            position: { lat: data.latLng.lat(), lng: data.latLng.lng() },
            pov: { heading: 70, pitch: -10 },
          });
          streetView.setVisible(true);
        }
      })
    }

    if (this.map?.googleMap && object.origin) {
      this.map.googleMap.setOptions({
        zoomControl: object.origin.zoomEnabled,
        gestureHandling: object.origin.panEnabled ? 'greedy' : 'none'
      });
    }

    if (this.map?.googleMap)
      this.getZones();

    if (this.map) {
      this.map.mapDragend.pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (this.map?.googleMap) {
          const center = this.map.googleMap.getCenter();
          const tilt = this.map.googleMap.getTilt();
          const zoom = this.map.googleMap.getZoom();

          if (center && tilt !== undefined && zoom !== undefined) {
            let viewerPositions = {
              lat: center.lat(),
              lon: center.lng(),
              tilt: tilt,
              zoom: zoom,
              projectId: this.project()?.id
            }

            this.updateUserLastPosition(viewerPositions)
          }
        }
      });
    }

    this.getUnits();
  }

  async updateUserLastPosition(viewerPositions: any) {
    try {
      if (!this.user().id) return;

      this.userService.updateLastPosition(viewerPositions, this.viewerPositions)
    } catch (error) {
      console.error(error)
    }
  }

  getZones() {
    this.zones = [];
    this.zoneService.getZones().pipe(takeUntil(this.destroy$)).subscribe({
      next: (zones: Zone[]) => {
        if (zones.length != 0) {
          this.polygonZones.forEach(p => {
            p.setMap(null)
            google.maps.event.clearInstanceListeners(p);
          })

          this.polygonZones = [];

          zones.forEach((t: Zone) => {

            if (t.polygon?.length != 0) {
              this.setZone(t)
            }
          });
        }
      },
      error: (err) => console.error(err)
    });
  }

  onZoneDateChanged(event: { id: "startDate" | "endDate", date: string }) {
    let zone = this.currentZone();

    if (zone) {
      zone[event.id] = event.date;
      this.currentZone.set(zone)
    }
  }

  closeFloatingWindow() {
    this.showFloatingWindow = false;

    if (typeof this.markers == 'object' && this.markers !== null) {
      Object.values(this.markers).forEach((markerInfo: any) => {
        if (markerInfo.unit && markerInfo.unit.type == 'virtual' && markerInfo.marker) {
          google.maps.event.clearListeners(markerInfo.marker, 'dragend');
          markerInfo.marker.setDraggable(false);
        }
      });
    }
  }

  // ========================================
  // SHOWING UNITS ON MAP (INCLUDES LOADING ICONS)
  // ========================================

  getUnits() {
    this.unitService.getUnits().pipe(takeUntil(this.destroy$)).subscribe({
      next: (units: Unit[]) => {
        this.units = units;
        units.forEach(u => this.addUnitPoint(u));
        this.replayUnits = units.filter(unit => this.replayUnitNames.includes(unit.name));
        this.replayTrackers = this.replayUnits.filter(u => u.trackers && u.trackers.length).map(u => u.trackers[0].id);

        this.mapReady$.pipe(
          filter(ready => ready === true),
          take(1),
          takeUntil(this.destroy$)
        ).subscribe(() => {
          this.initTrackers();
        });

        Object.keys(this.markers).forEach(k => {
          let t = this.markers[k];
          this.updateMapTrack(t.lastTrack)
        });

      },
      error: (err) => console.error(err)
    }
    );
  }

  loadIcons() {
    return new Promise((resolve, reject) => {

      try {
        var icons = ['antenna', 'danger', 'drone', 'formwork', 'hook', 'wheel', 'worker', 'arrow'];

        var loadCount = 0;
        if (!Object.keys(this.icons).length) {
          this.icons = {};
          icons.forEach((icon) => {

            let xhr, url;
            url = `/models/${icon}.svg`;

            xhr = new XMLHttpRequest();
            xhr.open('GET', url, true);
            xhr.onload = async () => {
              this.icons[icon] = xhr.responseText;
              loadCount++;
              if (loadCount == icons.length) resolve(true)
            }
            xhr.send(null);
          })

        } else {
          resolve(true)
        }

      } catch (e) {
        console.error(e)
      }
    })
  }

  async initTrackers() {
    try {
      this.trackerObservableArray = [];
      await this.loadIcons();

      const validTrackerIds = new Set<string>(
        this.units
          .filter(u => u?.trackers?.length && u.trackers[0] != null)
          .map(u => (typeof u.trackers![0] === 'string' ? u.trackers![0] : (u.trackers![0] as any)?.id) as string)
          .filter(Boolean)
      );

      Object.keys(this.markers).forEach(trackerId => {
        if (validTrackerIds.has(trackerId)) return;

        const markerInfo = this.markers[trackerId];
        if (markerInfo?.marker) {
          google.maps.event.clearInstanceListeners(markerInfo.marker);
          markerInfo.marker.setMap(null);
        }
        if (markerInfo?.collider?.addToMap) {
          markerInfo.collider.addToMap(null);
        }

        delete this.markers[trackerId];
      });

      this.loadedMarkers = this.loadedMarkers.filter(lm => lm?.tracker && validTrackerIds.has(lm.tracker.id));
      this.virtualUnitsInfo = this.virtualUnitsInfo.filter(v => {
        const tid = v.unit?.trackers?.[0]?.id ?? v.unit?.trackers?.[0];
        return validTrackerIds.has(tid);
      });

      for (let i = 0; i < this.units.length; i++) {
        let unit = this.units[i];

        if (!unit || !unit.trackers || unit.trackers.length === 0 || unit.trackers[0] == undefined) continue;

        let trackerId = typeof unit.trackers[0] === 'string' ? unit.trackers[0] : unit.trackers[0]?.id;
        let unitId = unit.id;
        let unitName = unit.name;

        if (!unit.trackers?.length) continue;

        let unitTrackers = this.trackers.filter(t => t.id == unit.trackers![0] || t.id == (typeof unit.trackers![0] === 'string' ? unit.trackers![0] : unit.trackers![0]?.id));

        if (!unitTrackers.length) continue;

        // For now, all trackers will appear on map as online in replay mode
        let isConnected = this.playMode == "replay" ? true : this.tools.isWithinOfflineDelay(unitTrackers[0], this.trackerConfigs);
        let trackerName = unitTrackers[0]?.name;

        let unitIcon = unit.icon?.split(".")[0] || 'arrow';
        let traceColor = unit.color || 'default';
        let traceAlpha = unit.alpha || 0.5;
        let size = 50 * this.minUnitScale;
        let isVirtual = false;
        let svg, color;

        let icon = {
          url: ''
        }

        if (this.colors[traceColor])
          color = this.colors[traceColor];
        else
          color = traceColor;

        if (!this.icons[unitIcon]) {
          return;
        }

        svg = this.icons[unitIcon].replace('{{ color }}', color).replace('{{ iconColor }}', isConnected ? '#FFF' : '#000').replace('{{ triangleColor }}', color);
        var regSize = new RegExp('{{ size }}', 'g');
        svg = svg.replace(regSize, `${size}`);
        var regScale = new RegExp('{{ scale }}', 'g');
        svg = svg.replace(regScale, `${this.minUnitScale}`);

        icon.url = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);
        let url: any = await this.loadImageAsPNG(icon.url, 40, 40)

        let track: any = {
          tracker: trackerId,
          lat: 0,
          lon: 0,
          x: 0,
          y: 0,
          z: 0
        }

        if (typeof unit.trackers[0] !== 'string' && unit.trackers[0]?.lastTrack) {
          track = unit.trackers[0].lastTrack
        }

        if (unit.lastPosition) {
          track.x = unit.lastPosition.x;
          track.y = unit.lastPosition.y;
          track.z = unit.lastPosition.z;
          track.lat = unit.lastPosition.lat || 0;
          track.lon = unit.lastPosition.lon || 0;
        }

        if ((!track.lat && !track.lon) && (track.x && track.y)) {
          let lonLat = Tool3D.getLatLonFromXYZ2(this.projectXY[0] + track.x, this.projectXY[1] + track.y, this.altitude);

          track.lon = lonLat[1];
          track.lat = lonLat[0];
        }

        if (this.markers[trackerId]) {
          if (this.markers[trackerId].collider && this.markers[trackerId].collider.mapPolygon) {
            this.markers[trackerId].collider.unit = unit;
            this.markers[trackerId].collider.init();
          }

          if (this.markers[trackerId].marker) {
            this.markers[trackerId].marker.setIcon({ url })
            this.markers[trackerId].marker.setPosition(new google.maps.LatLng(track.lat, track.lon))
          }

          if (this.markers[trackerId].unit?.type == 'virtual') {
            isVirtual = true;
          }

          if (this.markers[trackerId].unit.type == 'virtual' && !this.virtualUnitsInfo.find(t => t.unit.id == this.markers[trackerId].unit.id)) {
            this.virtualUnitsInfo.push(this.markers[trackerId]);
          }

          this.markers[trackerId].unit = unit;
          this.markers[trackerId].color = traceColor;
          this.markers[trackerId].alpha = traceAlpha;
          this.markers[trackerId].lastTrack = track;

          if (this.trackerObservableArray.findIndex(tr => tr.id == trackerId) == -1) {
            this.trackerObservableArray.push({
              id: trackerId,
              unitId,
              unitName,
              name: trackerName,
              show: unit.visible,
              trace: unit.trace,
              showCollider: unit.collider.visible,
              color: traceColor,
              alpha: traceAlpha,
              isConnected
            });
          }
        } else {
          let marker = new google.maps.Marker({
            title: unit.name,
            label: {
              text: unit.name,
              color: 'black',
              className: `marker-label${unit.name == "Manitou MRT" ? " active" : ""}`
            },
            position: new google.maps.LatLng(track.lat, track.lon),
            icon: url,
            map: unit.visible && this.replayUnitNames.length == 0 ? this.map.googleMap : null,
            opacity: 0.8,
            zIndex: isVirtual ? 99 : 90,
            clickable: true
          });

          google.maps.event.addListener(marker, 'click', () => {
            let markers = this.loadedMarkers.map(lm => lm.marker);

            let previouslyClickedLabel = markers.map(m => {
              if (typeof m.getLabel === 'function') {
                return m.getLabel();
              }

              return null;
            }).find(label => label && /active/.test(label.className));

            if (previouslyClickedLabel) {
              let prevClickedMarker = markers.find(m => {
                if (typeof m.getLabel === 'function') {
                  return m.getLabel() == previouslyClickedLabel;
                }

                return false;
              });

              if (prevClickedMarker && typeof prevClickedMarker.setLabel === 'function') {
                prevClickedMarker.setLabel({ ...previouslyClickedLabel, className: `marker-label` });
                prevClickedMarker.setZIndex(10);
              }
            }

            let clickedMarker = markers.find(m => m.getTitle() == marker.getTitle());

            if (!clickedMarker) return;

            if (typeof clickedMarker.setLabel === 'function' && typeof marker.getLabel === 'function') {
              const currentLabel = marker.getLabel();
              clickedMarker.setLabel({ ...(currentLabel as object), className: "marker-label active" });
            }

            clickedMarker.setZIndex(100);

            if (this.zoneEditor && this.editingPoint && this.useUnitPosition) {
              this.zoneEditor.updatePoint(this.editingPoint, { lat: clickedMarker.position.lat(), lng: clickedMarker.position.lng() });
              this.useUnitPosition = false;
              this.editingPoint = null;
            }
          });

          let collider = null;

          if (unit.collider) {
            let options = {
              unit: unit,
              XYOrigin: this.projectXY,
            }

            collider = this.getCollider(options);
          }

          this.markers[trackerId] = {
            marker,
            name: trackerName,
            color: traceColor,
            alpha: traceAlpha,
            collider,
            unit,
            lastTrack: track
          };

          this.trackerObservableArray.push({
            id: trackerId,
            unitId,
            unitName,
            name: trackerName,
            show: this.replayUnitNames.length ? true : unit.visible,
            trace: unit.trace,
            showCollider: this.replayUnitNames.length ? true : unit.collider.visible,
            color: traceColor,
            alpha: traceAlpha,
            isConnected: this.replayUnitNames.length ? true : isConnected
          });

          const t = {
            id: trackerId,
            unitId,
            name: trackerName,
            show: this.replayUnitNames.length ? true : unit.visible,
            trace: unit.trace,
            showCollider: this.replayUnitNames.length ? true : unit.collider.visible,
            color: traceColor,
            alpha: traceAlpha,
            isConnected: this.replayUnitNames.length ? true : isConnected
          }

          if (this.markers[trackerId].unit.type == 'virtual' && !this.virtualUnitsInfo.find(t => t.unit.id == this.markers[trackerId].unit.id)) {
            this.virtualUnitsInfo.push(this.markers[trackerId]);
          }

          this.markers[trackerId].marker.addListener("dblclick", () => {
            this.onTrackerDoubleClick(this.markers[trackerId]);
          });

          this.loadedMarkers.push({ tracker: t, marker, collider })
        }
      }

      this.units.forEach(u => this.addUnitPoint(u));

      if (!this.isInitialized) {
        this.loadedMarkers.forEach((t) => {
          if (!t || !t.tracker) return;

          if (this.project()?.showDisconnectTrackers && this.replayUnitNames.length == 0 && t.tracker.show) {
            t.marker.setMap(this.map.googleMap);
          }
          else {
            if (t.tracker.isConnected && this.replayUnitNames.length == 0 && t.tracker.show) {
              t.marker.setMap(this.map.googleMap);
            } else {
              t.marker.setMap(null);
            }
          }
        });

        this.isInitialized = true;
      }

      if (!this.hasStartedSendingTracks) {
        this.hasStartedSendingTracks = true;
        this.startVirtualTrackInterval();
      }
    } catch (err) {
      console.error(err);
    }
  }

  getTrackers() {
    this.trackerService.getTrackers().pipe(takeUntil(this.destroy$)).subscribe(trackers => {
      this.trackers = trackers;
      this.renderTabletMarkers();
    })
  }

  // Draws/refreshes one lightweight marker per "Tablet" tracker (phone/tablet connection
  // heartbeat - see getTrackerType() server-side), independently from the Unit-driven
  // initTrackers()/updateMapTrack() pipeline above, which only ever creates markers for
  // trackers referenced by a Unit. mapReady$ guards against running before this.map exists
  // (same guard getUnits() uses for initTrackers()).
  renderTabletMarkers() {
    this.mapReady$.pipe(
      filter(ready => ready === true),
      take(1),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      const tablets = this.trackers.filter(t => t.type === 'Tablet');
      this.tabletTrackerIds = new Set(tablets.map(t => t.id));

      Object.keys(this.tabletMarkers).forEach(trackerId => {
        if (tablets.find(t => t.id === trackerId)) return;
        this.tabletMarkers[trackerId]?.setMap(null);
        delete this.tabletMarkers[trackerId];
      });

      tablets.forEach(t => this.updateTabletMarker(t.id, t.name, t.lastTrack));
    });
  }

  // Shared by renderTabletMarkers() (initial load) and updateMapTrack()'s live-socket path
  // (a new Track for a tablet has no Unit/collider, so it would otherwise be silently
  // dropped by that method's "no marker for this tracker" guard).
  updateTabletMarker(trackerId: string, trackerName: string, track: any) {
    if (!track || (!track.lat && !track.lon) || !this.map?.googleMap) return;

    const position = new google.maps.LatLng(track.lat, track.lon);

    if (this.tabletMarkers[trackerId]) {
      this.tabletMarkers[trackerId].setPosition(position);
      return;
    }

    this.tabletMarkers[trackerId] = new google.maps.Marker({
      position,
      map: this.map.googleMap,
      title: trackerName,
      label: {
        text: trackerName,
        color: 'black',
        className: 'marker-label'
      },
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: '#4285F4',
        fillOpacity: 1,
        strokeColor: '#FFFFFF',
        strokeWeight: 2
      },
      zIndex: 80,
      clickable: false
    });
  }

  onTrackerDoubleClick(marker: any) {
    if (this.zoneCreator) {
      return;
    } else {
      this.editingZone = false;
    }
    if (!this.clickedMarkerInfo || this.clickedMarkerInfo != marker || !this.showFloatingWindow) {
      this.clickedMarkerInfo = marker;
      this.showFloatingWindow = true;

      if (this.clickedMarkerInfo.unit.type == 'virtual' && this.clickedMarkerInfo.marker && this.liveState == true) {
        this.clickedMarkerInfo.marker.setDraggable(true);

        this.clickedMarkerInfo.marker.addListener('dragend', () => {
          const newPosition = this.clickedMarkerInfo.marker.getPosition();
          const newLat = newPosition.lat().toFixed(7);
          const newLon = newPosition.lng().toFixed(7);

          marker.lastTrack.lat = Number(newLat);
          marker.lastTrack.lon = Number(newLon);
          marker.unit.lastPosition.lat = Number(newLat);
          marker.unit.lastPosition.lon = Number(newLon);


          this.clickedMarkerInfo = { ...marker };
          let selectVirtual = this.virtualUnitsInfo.find(t => t.unit.id == marker.unit.id);
          selectVirtual.lastTrack.lat = Number(newLat);
          selectVirtual.lastTrack.lon = Number(newLon);
          this.handleSaveData(selectVirtual);
        });
      }

      Object.values(this.markers).forEach((markerInfo: any) => {
        if (markerInfo !== this.clickedMarkerInfo && markerInfo.marker) {
          markerInfo.marker.setDraggable(false);
          google.maps.event.clearListeners(markerInfo.marker, 'dragend');
        }
      });
    }
  }

  handleSaveData(eventData: any) {
    const { lat, lon, z } = eventData.unit.lastPosition;
    const trackerId = eventData.unit.trackers[0]?.id ?? eventData.unit.trackers[0];

    this.trackService.addTrack({
      tracker: trackerId,
      lat,
      lon,
      z
    }).pipe(takeUntil(this.destroy$)).subscribe({
      error: err => console.error(err)
    });

    this.unitService.updateUnit({
      id: eventData.unit.id,
      lastPosition: { lat, lon, z }
    }).pipe(takeUntil(this.destroy$)).subscribe({
      error: err => console.error(err)
    });

    const virtualSelect = this.virtualUnitsInfo.find(t => t.unit.id == eventData.unit.id);

    if (!virtualSelect) return;

    virtualSelect.unit.lastPosition.lat = lat;
    virtualSelect.unit.lastPosition.lon = lon;
    virtualSelect.unit.lastPosition.z = z;
    virtualSelect.lastTrack.lat = lat;
    virtualSelect.lastTrack.lon = lon;
    virtualSelect.lastTrack.z = z;
  }

  getCollider(options: any) {
    try {
      let unit = options.unit;
      if (unit.collider.geometry == null) return null;
      if (this.modelData?.origin)
        options.origin = this.modelData.origin;
      let collider = new MapCollider(options)
      collider.init();
      if (unit.collider.visible || this.replayUnitNames.length) {
        collider.addToMap(this.map);

        if (!unit.trackers?.length || !unit.lastPosition?.lat || !unit.lastPosition?.lon) return collider;

        let trackerId = typeof unit.trackers[0] === 'string' ? unit.trackers[0] : unit.trackers[0]?.id;
        let child = null;
        let parent = this.getParentUnit(trackerId || '');
        let lastPos = unit.lastPosition;

        if (unit.collider.geometry == "Crane" && unit.crane && (unit.crane.jibTracker || unit.crane.hookTracker)) {
          const tracker = unit.crane.jibTracker || unit.crane.hookTracker;
          const trackerId = typeof tracker === 'string' ? tracker : tracker?.id;
          if (trackerId) {
            child = this.markers[trackerId];
          }
        }

        // Create proper objects for MapCollider methods
        if (lastPos && lastPos.lat && lastPos.lon) {
          const position: Position = {
            lat: lastPos.lat,
            lon: lastPos.lon,
            x: lastPos.x || 0,
            y: lastPos.y || 0,
            z: lastPos.z || 0
          };

          // Create a Track object for updateLatLonPosition
          const track: Track = { ...defaultTrack, ...position };

          if (child && child.lastTrack) {
            collider.updateLatLonRotation(position, child.lastTrack)
          } else {
            collider.updateLatLonPosition(track)
          }

          if (parent && (parent as any).collider && (parent as any).lastTrack) {
            (parent as any).collider.updateLatLonRotation((parent as any).lastTrack, position)
          }
        }
      }

      return collider;

    } catch (e) {
      console.error(e)
      return null;
    }
  }

  getParentUnit(trackerId: string): any {
    let parent = null;
    Object.keys(this.markers).forEach(k => {
      let t = this.markers[k];

      if (t.unit && t.unit.collider && t.unit.collider.geometry == "Crane" && (t.unit.crane.jibTracker == trackerId || t.unit.crane.hookTracker == trackerId)) {
        parent = t;
      }
    })

    return parent;
  }

  sendVirtualTracks(): void {
    this.virtualUnits = [];

    if (this.liveState == true) {
      Object.keys(this.markers).forEach((k) => {
        if (this.markers[k].unit.type == 'virtual') {
          this.trackerObservableArray.forEach((tracker) => {

            if (tracker.unitId == this.markers[k].unit.id && tracker.show == true) {
              this.virtualUnits.push(this.markers[k]);
            }
          })
        }
      });
    }

    this.virtualUnits.forEach((virtualUnit) => {
      this.sendTrack(virtualUnit);
    });
  }

  sendTrack(virtualUnit: any): void {
    const query = {
      trackerName: virtualUnit.name,
      tracker: virtualUnit.unit.trackers[0],
      lat: virtualUnit.unit.lastPosition.lat,
      lon: virtualUnit.unit.lastPosition.lon,
      z: virtualUnit.unit.lastPosition.z,
      database: this.project()?.database
    };

    this.trackService.addTrackVirtual(query).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => { },
      error: (err) => console.error(err)
    });
  }


  // ========================================
  // UNIT MODAL AND TRACKER SETTINGS
  // ========================================
  traceUnitModal() {
    const modal = this.dialog.open(TracesDialogComponent, {
      data: {
        trackers: this.trackerObservableArray,
        trackerConfigs: this.trackerConfigs,
        mode: this.playMode,
        startDate: this.startDate,
        endDate: this.endDate,
        model: this.modelData,
        updateTrack: (t: Track) => this.updateMapTrack(t),
      },
      width: '800px',
      height: this.trackerObservableArray.length > 10 ? '700px' : 'auto',
      maxHeight: '90vh'
    });

    const sub = modal.componentInstance.onAdd.pipe(takeUntil(this.destroy$)).subscribe(async (updatedTrackers: any) => {
      let { trackers, action, value } = updatedTrackers;

      if (action == 'showConnect') {
        const newShowDisconnectValue = value;

        this.loadedMarkers.forEach((t) => {
          if (t && t.tracker.show) {
            if (value === true) {
              t.marker.setMap(this.map.googleMap);
            } else {
              if (t.tracker.isConnected) {
                t.marker.setMap(this.map.googleMap);
              } else {
                t.marker.setMap(null);
              }
            }
          }
        });

        if (!this.project()) return;

        const json: PutProject = {
          id: this.project().id,
          showDisconnectTrackers: newShowDisconnectValue
        };

        this.projectService.setProject(json as PutProject).pipe(takeUntil(this.destroy$)).subscribe({
          next: (res) => {
            const updatedProject: Project = { ...this.project(), showDisconnectTrackers: newShowDisconnectValue };
            this.projectService.setCurrentProject(updatedProject);
          },
          error: (err) => {
            console.error("Erreur lors de la mise à jour du projet:", err);
          }
        });

        return;
      }

      let trackerIds = trackers.map((t: Tracker) => t.id);
      let loadedMarkers = this.loadedMarkers.filter(m => trackerIds.includes(m.tracker.id));
      let loadedTraces = this.loadedTraces.filter(trace => trackerIds.includes(trace?.tracker?.id));

      let updateCounter = 0;
      let promises: Promise<any>[] = [];

      loadedMarkers.forEach(loadedMarker => {
        let promise = new Promise((resolve, reject) => {
          updateCounter++;
          let tracker = trackers.find((t: Tracker) => t.id == loadedMarker.tracker.id);
          if (!tracker) return resolve("");
          if (loadedMarker.tracker.show !== tracker.show || loadedMarker.tracker.showCollider !== tracker.showCollider || loadedMarker.tracker.trace !== tracker.trace || loadedMarker.tracker.color !== tracker.color) {
            let unit = this.markers[tracker.id].unit;
            let unitIndex = this.units.findIndex(u => u.id == unit.id);
            if (unitIndex >= 0 && this.units[unitIndex]) {
              this.units[unitIndex].visible = tracker.show;
              this.units[unitIndex].trace = tracker.trace;
              this.units[unitIndex].color = tracker.color;
            }

            unit.collider.visible = tracker.showCollider;
            unit.color = tracker.color;

            this.unitService.updateUnit({ id: unit.id, visible: tracker.show, trace: tracker.trace, collider: unit.collider, color: tracker.color }).pipe(takeUntil(this.destroy$)).subscribe(res => {
              if (updateCounter == loadedMarkers.length) {
                // this.initTrackers();
              }

              this.markers[tracker.id].color = tracker.color;

              // this.markers[tracker.id].tracker.color = tracker.color;
              // this.markers[tracker.id].trace = tracker.trace;

              const index = this.trackerObservableArray.findIndex(t => t.id === tracker.id);
              if (index !== -1) {
                this.trackerObservableArray.splice(index, 1, tracker);
              }

              loadedMarker.tracker.trace = tracker.trace;
              loadedMarker.tracker.show = tracker.show;
              loadedMarker.tracker.showCollider = tracker.showCollider;
              resolve("ok");
            });
          } else {
            resolve("ok");
          }
        });

        promises.push(promise);
      });

      await Promise.all(promises);

      if (action == 'trace') {
        loadedTraces.forEach((t: any) => {
          if (t && !t.tracker[action]) {
            t[action].getPath().clear()
            t[action].setMap(null)
          }
        })
      } else if (action == 'reset-trace') {
        loadedTraces.forEach((t: any) => {
          t['trace'].getPath().clear()
          t['trace'].setMap(null)
        })
      } else if (action == 'show') {
        loadedMarkers.forEach(loadedMarker => {
          if (!loadedMarker || !loadedMarker.tracker) return;

          if (this.project()?.showDisconnectTrackers == false) {
            if (loadedMarker.tracker[action] && loadedMarker.tracker.isConnected) {
              loadedMarker.marker.setMap(this.map.googleMap)
            } else {
              loadedMarker.marker.setMap(null);
            }
          } else {
            if (loadedMarker.tracker[action]) {
              loadedMarker.marker.setMap(this.map.googleMap);
            }
            else if (!loadedMarker.tracker[action]) {
              loadedMarker.marker.setMap(null);
            }
          }
        })

        this.sendVirtualTracks()

      } else if (action == 'showCollider') {
        loadedMarkers.forEach(loadedMarker => {
          if (!loadedMarker || !loadedMarker.collider || !loadedMarker.tracker) return;

          if (!loadedMarker.tracker[action]) {
            loadedMarker.collider.addToMap(null);
          } else if (loadedMarker.tracker[action]) {
            loadedMarker.collider.init();
            loadedMarker.collider.addToMap(this.map)
          }
        });
      }
    });

    modal.afterClosed().subscribe(() => {
      if (sub)
        sub.unsubscribe();
    })
  }

  // ========================================
  // ZONE CREATION
  // ========================================
  zoneModal() {
    const modal = this.dialog.open(ZonesDialogComponent, {
      data: { zones: this.zones },
      width: '950px',
      restoreFocus: false,
      maxHeight: '90vh'
    })

    const sub = modal.componentInstance.onAdd.pipe(takeUntil(this.destroy$)).subscribe((results) => {
      this.zones = results.zones;
      this.polygonZones.forEach(t => {
        const path = t.getPaths();
        path.pop()
      });

      this.polygonZones = [];
      this.zonePoints.forEach(p => p.marker ? p.marker.setMap(null) : "");
      this.zonePoints = [];
      results.zones.forEach((t: Zone) => {
        if (t.visible && t.polygon && t.polygon.length != 0) {
          this.setZone(t)
        }
      });
    });

    modal.afterClosed().subscribe((res) => {
      if (sub) sub.unsubscribe();
      if (this.zoneEditor)
        this.zoneEditor.disable();

      if (res) {
        this.createZone = res.createZone;
        if (this.createZone) this.initializeZone();
        if (res.editZone && res.zone) this.editZone(res.zone);
        if (res.duplicateZone && res.zone) this.duplicateZone(res.zone);
      }
    })
  }

  initializeZone() {
    this.editingZone = true;
    this.zoneCreator = new ZoneCreate({
      map: this.map,
      onZoneComplete: (path: any) => {
        this.map.googleMap?.setOptions({ draggableCursor: '' });
        this.zoneCreator?.disable();
        this.zoneCreator = null;
        this.showTrackerPosition = false;

        let polygon = [];
        let _polygon = [];

        for (let i = 0; i < path.length; i++) {
          const point = Tool3D.getXYFromLatLonAlt(path.getAt(i).lat(), path.getAt(i).lng(), 0);
          let latLon = { lat: path.getAt(i).lat(), lng: path.getAt(i).lng() };
          polygon.push(point);
          _polygon.push({ lat: latLon.lat, lng: latLon.lng })
        }

        let zone = {
          polygon,
          name: "Untitled " + (this.zones.length + 1),
          color: 'primary',
          type: "polygon",
          visibility: true
        };

        this.zoneService.addZone(zone as PostZone).pipe(takeUntil(this.destroy$)).subscribe(res => {
          let polygon = new google.maps.Polygon({
            paths: _polygon,
            strokeColor: 'red',
            fillColor: '#5bc0de',
            strokeWeight: 2,
            fillOpacity: 0.45,
            clickable: true
          });

          polygon.setMap(this.map.googleMap || null)
          this.polygonZones.push(polygon);

          this.setZone(res);
          this.currentZone.set(res)
          this.mergingPoint$.next(false);
          this.ctrlKeyDown = false;
        }, err => console.error(err))
      },
    });

    this.zoneCreator.enable();
    this.map.googleMap?.setOptions({ draggableCursor: 'crosshair' });
    this.focusMap();
  }

  async setZone(zone: Zone) {
    try {
      if (zone.polygonObj && zone.polygonObj instanceof google.maps.Polygon) {
        zone.polygonObj.setMap(null);
        zone.polygonObj = null;
      }

      let positions: { lat: number; lng: number }[] = [];
      let latLon: number[];
      let zonePointsToRemove = this.zonePoints.filter(z => z.zoneId == zone.id || (!z.zoneId && !zone.id));

      if (zonePointsToRemove.length) { // this way only zone points that actually belong to a zone are kept
        zonePointsToRemove.forEach(z => ['marker', 'duplicateMarker', 'hoverMarker'].forEach(key => z[key].setMap(null)));
        this.zonePoints = this.zonePoints.filter(z => z.zoneId && z.zoneId != zone.id);
      }

      if (zone.polygon) {
        zone.polygon.forEach((t: number[]) => {
          if (t.length >= 2) {
            latLon = Tool3D.getLatLonFromXYZ(t[0] || 0, t[1] || 0, 0)
            positions.push({ lat: latLon[1] || 0, lng: latLon[0] || 0 });

            let zonePoint = {
              id: Math.floor(Math.random() * 100000),
              x: t[0],
              y: t[1],
              marker: new google.maps.Marker({
                position: { lat: latLon[1] || 0, lng: latLon[0] || 0 },
                map: this.map.googleMap,
                visible: false,
                icon: {
                  url: "/img/viewer/red-dot.png",
                  size: new google.maps.Size(8, 8),
                  origin: new google.maps.Point(0, 0),
                  anchor: new google.maps.Point(4, 4)
                },
              }),
              hoverMarker: new google.maps.Marker({
                position: { lat: latLon[1] || 0, lng: latLon[0] || 0 },
                map: this.map.googleMap,
                visible: false,
                icon: {
                  url: "/img/viewer/green-dot.png",
                  size: new google.maps.Size(10, 10),
                  origin: new google.maps.Point(0, 0),
                  anchor: new google.maps.Point(5, 5)
                },
              }),
              duplicateMarker: new google.maps.Marker({
                position: { lat: latLon[1] || 0, lng: latLon[0] || 0 },
                map: this.map.googleMap,
                visible: false,
                zIndex: 200,
                icon: {
                  url: "/img/viewer/black-blue-dot.png",
                  size: new google.maps.Size(8, 8),
                  origin: new google.maps.Point(0, 0),
                  anchor: new google.maps.Point(4, 4),
                },
              }),
              zoneId: zone.id,
              visible: zone.visible
            };

            zonePoint.duplicateMarker.addListener('mouseover', () => {
              if (this.zoneEditor)
                zonePoint.duplicateMarker.setVisible(false)
            });

            this.zonePoints.push(zonePoint);
          }
        });
      }

      if (zone.id == this.editedZone()?.id || !zone.id) {
        let zonePoints = this.zonePoints.filter(p => p.zoneId == zone.id || (!p.zoneId && !zone.id));
        zonePoints.forEach(point => point.duplicateMarker.setVisible(false));

        // check if zonePoints has any duplicates
        zonePoints.forEach(point => {
          if (this.isDuplicatePoint(point, zonePoints.filter(p2 => p2.id != point.id))) {
            point.duplicateMarker.setVisible(true);
          }
        });
      }

      const zoneColor = this.convertColors(zone.color);

      let polygon = new google.maps.Polygon({
        paths: positions,
        strokeColor: zoneColor,
        fillColor: zoneColor,
        strokeWeight: 2,
        fillOpacity: 0.45,
        clickable: true
      });

      polygon.addListener('click', (event: google.maps.MapMouseEvent) => {
        this.editingZone = true;
        this.currentZone.set(zone)

        this.polygonZones.forEach(p => {
          p.setOptions({
            strokeWeight: 2
          });
        })

        if (this.zoneEditor)
          this.zoneEditor.disable();

        polygon.setOptions({
          strokeWeight: 4
        });
      });

      this.polygonZones.push(polygon);

      if (zone.visible) {
        polygon.setMap(this.map.googleMap || null);
      }
      zone.polygonLatLon = polygon.getPath();
      zone.polygonObj = polygon;
      let zIndex = this.zones.findIndex(z => z.id == zone.id)

      if (zIndex >= 0) {
        this.zones[zIndex] = zone;
      } else {
        this.zones.push(zone);
      }

      return zone;
    } catch (e) {
      console.error(e)
      return null;
    }
  }

  saveZone(zone: any) {
    let zoneToSave = Object.assign({}, { ...zone });

    if (zoneToSave.polygonObj)
      zoneToSave.polygonObj.setMap(null);

    delete zoneToSave.polygonObj;
    delete zoneToSave.edited;
    delete zoneToSave.polygonLatLon;

    if (this.showTrackerPosition)
      this.closeTrackerPosition();

    this.editingPoint = null;
    this.showContextMenu = false;

    if (zone.id) {
      this.zoneService.updateZone(zoneToSave).pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          // Nettoyage commun au succès et à l'erreur
          this.editingZone = false;
          this.zoneEditor?.disable();
          this.zoneEditor = null;
        })
      ).subscribe({
        next: updatedZone => {
          // this.setZone(updatedZone);
          this.pop.toast(this.translate.instant('viewer.zone.edited'), "success", 4000, "edit");
        },
        error: err => {
          // this.setZone(zone);
          this.pop.toast(this.translate.instant('viewer.zone.not_edited'), "danger", 4000, "edit");
          console.error(err)
        }
      })
    } else {
      this.zoneService.addZone(zoneToSave).pipe(takeUntil(this.destroy$), finalize(() => {
        // Nettoyage commun au succès et à l'erreur
        this.editingZone = false;
        this.zoneEditor?.disable();
        this.zoneEditor = null;
        this.duplicatingZone = false;
      })).subscribe({
        next: newZone => {
          this.setZone(newZone);
          this.pop.toast(this.translate.instant('viewer.zone.yes_duplicate'), "success", 4000, "duplicate");
        },
        error: err => {
          console.error(err);
          this.pop.toast(this.translate.instant('viewer.zone.not_duplicate'), "danger", 4000, "duplicate");
        }
      });
    }
  }

  deleteZone(zone: any) {
    const trad = {
      title: this.translate.instant('form.delete') + " " + zone.name,
      content: this.translate.instant('viewer.zone.delete') + " " + zone.name + " ?"
    }

    const dialogRef = this.dialog.open(ConfirmComponent, {
      width: '300px',
      data: {
        title: trad.title,
        content: trad.content
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.ok) {
        this.zoneService.removeZone(zone.id).pipe(takeUntil(this.destroy$)).subscribe(res => {
          this.currentZone.set(null)
          this.editingZone = false;
          let zIndex = this.zones.findIndex(z => z.id == zone.id);

          if (zIndex >= 0) {
            this.zones.splice(zIndex, 1)
          }
        }, err => console.error(err))
      }
    });
  }

  updateGeolocationIcon(position: any) {
    if (!this.map?.googleMap) {
      return;
    }

    if (this.geolocationMarker)
      this.geolocationMarker.setMap(null)

    const svgMarker = {
      path: `M12 3v5 M12 16v5 M3 12h5 M16 12h5 M12 10a2 2 0 1 1 0 4a2 2 0 1 1 0-4 M12 0C5.37 0 0 5.37 0 12s5.37 12 12 12 12-5.37 12-12S18.63 0 12 0z`,
      fillColor: "transparent",
      fillOpacity: 0,
      strokeColor: "red",
      strokeWeight: 1.5,
      scale: 1,
      anchor: new google.maps.Point(12, 12),
    }

    this.geolocationMarker = new google.maps.Marker({
      position: { lat: position.lat, lng: position.lon },
      icon: svgMarker,
      map: this.map.googleMap,
      visible: true,
      zIndex: 10
    });

    this.map.googleMap.panTo(new google.maps.LatLng(position.lat, position.lon));
  }

  openTrackerPosition() {
    this.showTrackerPosition = true;
  }

  submitTrackerPosition(position: any) {
    if (this.zoneCreator) {
      this.zoneCreator.addPoint(position.lat, position.lon, true);

      if (this.geolocationMarker) {
        this.geolocationMarker.setMap(null);
        this.geolocationMarker = null;
      }
    } else if (this.zoneEditor && this.editingPoint) {
      this.zoneEditor.updatePoint(this.editingPoint, { lat: position.lat, lng: position.lon });
      this.showTrackerPosition = false;
      this.editingPoint = null;

      if (this.geolocationMarker) {
        this.geolocationMarker.setMap(null);
        this.geolocationMarker = null;
      }
    }
  }

  closeTrackerPosition() {
    this.showTrackerPosition = false;

    if (this.geolocationMarker) {
      this.geolocationMarker.setMap(null);
      this.geolocationMarker = null;
    }

    if (this.zoneEditor) {
      this.zoneEditor.blurPoint();
      this.editingPoint = null;
    }
  }

  addUnitPoint(unit: any) {
    if (this.unitPoints.find(u => u.name == unit.name) || !unit.lastPosition?.lat || !unit.lastPosition?.lon || !unit.visible || !unit.trackers?.length) return;

    let marker = this.markers[unit.trackers[0].id]?.marker;

    if (!marker) return;

    const { lat, lon } = unit.lastPosition;
    const [x, y] = Tool3D.getXYFromLatLonAlt(lat, lon);
    let unitPosition = { ...unit.lastPosition, x, y, name: unit.name, isUnit: true, visible: true, marker };
    this.unitPoints.push(unitPosition);
  }

  focusMap() {
    if (!this.map?.googleMap) return;

    const mapDiv = this.map.googleMap.getDiv() as HTMLElement;
    mapDiv.setAttribute("tabindex", "0");
    mapDiv.focus();
  }

  isDuplicatePoint(point: any, zonePoints: any[]): boolean {
    return zonePoints.find(p => p.x == point.x && p.y == point.y);
  }

  hoverNearestPoint() {
    this.mergingPoint$.next(false);
    let zonePoints = this.zonePoints;

    if (this.zoneEditor) {
      let editedZoneId = this.editedZone()?.id
      zonePoints = this.zonePoints.filter(z => z.zoneId != editedZoneId)
    }

    zonePoints.filter(z => z.visible).forEach(p => {
      p.hoverMarker.setVisible(false);

      if (!p.duplicateMarker.getVisible())
        p.marker.setVisible(true);
    });

    this.unitPoints.forEach(u => {
      if (u.marker && typeof u.marker.getLabel === 'function') {
        const currentLabel = u.marker.getLabel();
        u.marker.setLabel({ ...currentLabel, className: "marker-label" });
      }
    });

    zonePoints = zonePoints.concat(this.unitPoints);
    const mouseEvent = this.lastMouseEvent;

    if (!mouseEvent) return;

    let { lat, lon, x = 0, y = 0 } = this.getMapCoords(mouseEvent.clientX, mouseEvent.clientY);
    let nearestPoint = Tool3D.getNearestPoint([x, y], zonePoints.filter(p => p.visible));

    if (nearestPoint) {
      x = nearestPoint.x;
      y = nearestPoint.y;
      let newLatLon = Tool3D.getLatLonFromXYZ(x, y, 0);
      nearestPoint.lat = newLatLon[1];
      nearestPoint.lon = newLatLon[0];
      // nearestPoint.marker.setVisible(false);

      if (nearestPoint.isUnit && nearestPoint.marker && typeof nearestPoint.marker.getLabel === 'function') {
        const currentLabel = nearestPoint.marker.getLabel();
        nearestPoint.marker.setLabel({ ...currentLabel, className: "marker-label snapping" });
      } else {
        nearestPoint.hoverMarker.setVisible(true);
      }

      this.mergingPoint$.next(nearestPoint);
    }
  }

  convertColors(color: string): string {
    let array = [
      { title: 'primary', value: '#87CEEB' },
      { title: 'danger', value: '#bb2124' },
      { title: 'success', value: "#22bb33" },
      { title: 'warning', value: "#f0ad4e" },
      { title: 'info', value: "#5bc0de" }
    ]

    array.forEach(t => {
      if (color === t.title) color = t.value;
    })

    return color
  }


  // ========================================
  // ZONE EDITING
  // ========================================
  editZone(zone: Zone) {
    if (this.zoneEditor)
      this.zoneEditor.disable();

    this.zones.forEach(z => z.id == zone.id ? zone.edited = true : zone.edited = false);
    this.currentZone.set(zone);
    this.editedZone.set({ ...zone });
    let zonePoints = this.zonePoints.filter(p => p.zoneId == zone.id);

    // make duplicate points visible
    zonePoints.forEach(point => {
      if (this.isDuplicatePoint(point, zonePoints.filter(p2 => p2.id != point.id))) {
        point.duplicateMarker.setVisible(true);
      }
    });

    this.zoneEditor = new ZoneTransform({
      map: this.map, zone, polygon: zone.polygonLatLon, polygonObj: zone.polygonObj, zonePoints: this.zonePoints,
      mergingPointObs: this.mergingPoint$.asObservable(),
      onUpdate: (polygon: any) => {
        this.useUnitPosition = false;

        zone.polygon = polygon.map((p: any) => {
          if (this.currentZonePoint && p.name == this.currentZonePoint.name) {
            this.currentZonePoint = p;
          }

          return Tool3D.getXYFromLatLonAlt(p.lat, p.lng, 0)
        });

        this.setZone(zone);
        this.zoneEditor?.init({ zone, polygon: zone.polygonLatLon, polygonObj: zone.polygonObj, zonePoints: this.zonePoints });
        this.focusMap();
      },
      onMarkerClick: ({ marker, point }: { marker: any, point: Point }) => {
        this.editingPoint = point;
      },
      onMarkerRightClick: ({ point }: { point: Point }) => {
        this.useUnitPosition = false;
        this.editingPoint = point;
        let { clientX, clientY } = this.getScreenCoords(point.lat, point.lng);
        this.showContextMenu = true;
        this.mapRect = this.map.googleMap?.getDiv()?.getBoundingClientRect() || new DOMRect();
        this.menuPosition = { x: clientX, y: clientY };
      }
    });

    this.editingZone = true;
  }

  onZonePointUpdate() {
    if (this.zoneEditor && this.editingPoint) {
      this.zoneEditor.updatePoint(this.editingPoint, this.editingPoint)
    }
  }

  cancelEditZone() {
    const zone = this.currentZone();
    if (!zone) return;

    delete zone.edited;
    this.editingZone = false;

    if (this.showTrackerPosition)
      this.closeTrackerPosition();

    this.zoneEditor?.disable();
    this.zoneEditor = null;
    this.editingPoint = null;
    this.showContextMenu = false;

    if (this.duplicatingZone) {
      this.duplicatingZone = false;

      this.zones.forEach(zone => {
        if (zone.polygonObj) {
          zone.polygonObj.setMap(null);
        }
      });

      if (this.polygonZones?.length) {
        this.polygonZones.forEach(polygonZone => {
          if (polygonZone)
            polygonZone.setMap(null);
        });

        this.polygonZones = [];
      }

      this.zones = this.zones.filter(z => !!z.id);
      this.zones.forEach(z => z.visible && z.polygon?.length ? this.setZone(z) : null);
    } else
      this.setZone(zone);
  }

  duplicateZone(zone: any) {
    let zoneToDuplicate = Object.assign({}, { ...zone });
    this.editingZone = false;
    this.duplicatingZone = true;

    zoneToDuplicate = {
      ...zoneToDuplicate,
      name: zone.name + " (copy)",
    };

    delete zoneToDuplicate.id;
    this.setZone(zoneToDuplicate);
    this.editZone(zoneToDuplicate);
  }

  deletePoint() {
    if (this.editingPoint && this.zoneEditor) {
      this.zoneEditor.removePoint(this.editingPoint);

      if (this.showContextMenu)
        this.showContextMenu = false;
    }
  }

  onContextMenuClose() {
    this.showContextMenu = false;

    if (!this.showTrackerPosition && !this.useUnitPosition)
      this.zoneEditor?.blurPoint();
  }

  getCurrentZoneObs() {
    return toObservable(this.currentZone);
  }

  getEditedZoneObs() {
    return toObservable(this.editedZone);
  }

  // ========================================
  // Project Model
  // ========================================
  updateModel(model: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.modelService.updateModel(model).pipe(takeUntil(this.destroy$)).subscribe({
        next: (res: any) => {
          console.log('----> request model :>>', model);
          console.log('----> res.model :>>', res.model);
          if (!this.modelData)
            this.modelData = res.model as Model;
          else {
            const update = {
              ...res.model,
              ...this.modelData,
            }

            this.modelData = update;
          }

          this.pop.toast(this.translate.instant('viewer.image.edited'), "success", 4000, "edit");
          resolve(res);
        },
        error: (err) => {
          this.pop.toast(this.translate.instant('viewer.image.not_edited'), "danger", 4000, "edit");
          console.error(err);
          reject(err);
        }
      });
    });
  }

  loadImageAsPNG(url: any, height: any, width: any) {
    return new Promise((resolve, reject) => {
      let sourceImage = new Image();

      sourceImage.onload = () => {
        let png = new Image();
        let cnv = document.createElement('canvas'); // doesn't actually create an element until it's appended to a parent,
        // so will be discarded once this function has done it's job
        cnv.height = height;
        cnv.width = width;

        let ctx = cnv.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get 2D context from canvas'));
          return;
        }

        ctx.drawImage(sourceImage, 0, 0, height, width);
        png.src = cnv.toDataURL(); // defaults to image/png
        //resolve(png)
        resolve(png.src);
      }
      sourceImage.onerror = reject;
      sourceImage.src = url;
    });
  }

  loadImageSize(src: string): Promise<{ width: number, height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = reject;
      img.src = src;
    });
  }

  cleanMapImageUrl(url: string): string {
    const base = '';

    if (!url) return '';

    const parts = url.split(base).filter(part => part.trim() !== '');
    const filename = parts[parts.length - 1];

    return `${base}${filename}`;
  }

  centerBtn(lat: any = null, lon: any = null, zoom: any = null) {
    lon = lon || this.mapObject.mapCenter?.lon;
    lat = lat || this.mapObject.mapCenter?.lat;

    console.log('----> lat :>>', lat);
    console.log('----> lon :>>', lon);

    let mapOptions: google.maps.MapOptions = {
      center: { lat, lng: lon },
      zoom: zoom || this.mapObject.origin.zoom || 15,
      tilt: this.mapObject.origin.tilt ? this.mapObject.origin.tilt : 0,
      mapTypeId: google.maps.MapTypeId.SATELLITE
    }

    this.map.googleMap?.setOptions(mapOptions);

    if (!zoom)
      this.userService.updateLastPosition({ lat: mapOptions.center?.lat || 0, lon: mapOptions.center?.lng || 0, zoom: mapOptions.zoom, tilt: 0, projectId: this.projectId }, this.viewerPositions)
  }

  // ========================================
  // ORIGIN AND IMAGE MODALS
  // ========================================
  originModal(): void {
    const origin = this.dialog.open(OriginDialogComponent, {
      width: '500px',
      panelClass: "origin-overlay",
      maxHeight: '90vh',
      data: {
        modelData: this.modelData,
        projectId: this.project()?.model,
        googleMap: this.map
      }
    });

    const sub = origin.componentInstance.onAdd.pipe(takeUntil(this.destroy$)).subscribe(async (results: any) => {
      console.log('----> results :>>', results);
      await this.updateModel(results.currentModel);
      this.mapObject = results.currentModel;

      if (this.modelData?.origin) {
        this.modelData.origin.mapAngle = 0;
      }

      this.mapAngle = 0;
      this.imageAngle = 0;


      this.userLastPosition = [{
        zoom: this.mapObject.origin.zoom,
        lat: this.mapObject.mapCenter?.lat,
        lon: this.mapObject.mapCenter?.lon,
        tilt: 0,
        projectId: this.projectId || ''
      }];

      console.log('----> this.modelData :>>', this.modelData);
      if (this.map) {
        await this.loadMap(this.modelData);
      }
    });

    origin.afterClosed().subscribe(() => {
      sub.unsubscribe();
    });
  }

  overlayModal() {
    this.mapStore.setMap(this.map);
    this.mapStore.setCurrentModel(this.modelData);

    const overlay = this.dialog.open(OverlayDialogComponent, {
      maxWidth: '95vw',
      maxHeight: '95vh',
      panelClass: "origin-overlay",
    });

    overlay.afterClosed().subscribe(async (shouldSave: boolean) => {
      if (!shouldSave) return;

      const model = this.mapStore.currentModel();
      if (!model) return;

      this.modelData = normalizeModel(model);
      await this.updateModel(this.modelData);
      await this.loadMap(this.modelData);
      this.mapStore.markClean();
    });
  }

  handleSaveImage() {
    try {
      this.modelData = this.editedImage();

      if (this.modelData) {
        let file = this.tools.base64ToFile(this.modelData.mapImage, "mapImage");

        if (file) {
          this.modelService.uploadImage("mapImage", file, this.modelData.id).subscribe({
            next: (res: any) => {
              this.updateModel(this.modelData);
            },
            error: (err) => {
              this.pop.toast(this.translate.instant('viewer.image.not_edited'), "danger", 4000, "save");
              console.error(err);
            }
          })
        } else {
          this.updateModel(this.modelData);
        }
      }

      this.editingImage = false;

      if (this.imageHandler)
        this.imageHandler.destroy();
    } catch (e) {
      console.error(e)
    }
  }

  cancelEditImage(image: any) {
    this.modelData = this.currentImage();
    this.editingImage = false;
    this.resizedCoords = null;
    clearTimeout(this.resizeTimeout);
    this.loadMap(image);

    // this.editingImage = false;
    if (this.imageHandler)
      this.imageHandler.destroy();
  }

  getCurrentImageObs() {
    return toObservable(this.currentImage);
  }

  getEditedImageObs() {
    return toObservable(this.editedImage);
  }

  getMapCoords(clientX: number, clientY: number) {
    if (!this.map?.googleMap) {
      throw new Error("Map is not available.");
    }

    const mapDiv = this.map.googleMap.getDiv();
    const mapElement = mapDiv as HTMLElement;
    const bounds = mapDiv.getBoundingClientRect();

    // Get the pixel relative to the map container
    const pixel = {
      x: clientX - bounds.left,
      y: clientY - bounds.top,
    };

    const projection = this.map.googleMap.getProjection();
    const mapBounds = this.map.googleMap.getBounds();

    if (!projection || !mapBounds) {
      throw new Error("Projection or map bounds are not available.");
    }

    // Get the world coordinates for the corners of the visible map
    const topRight = projection.fromLatLngToPoint(mapBounds.getNorthEast());
    const bottomLeft = projection.fromLatLngToPoint(mapBounds.getSouthWest());

    if (!topRight || !bottomLeft) {
      throw new Error("Could not get map bounds coordinates.");
    }

    const worldWidth = Math.abs(topRight.x - bottomLeft.x);
    const worldHeight = Math.abs(topRight.y - bottomLeft.y);

    // Translate pixel coordinates to world coordinates
    const point = new google.maps.Point(
      bottomLeft.x + (pixel.x / mapElement.offsetWidth) * worldWidth,
      topRight.y + (pixel.y / mapElement.offsetHeight) * worldHeight
    );

    const latLng = projection.fromPointToLatLng(point);

    if (!latLng) {
      throw new Error("Could not convert point to lat/lng coordinates.");
    }

    const pointXY = Tool3D.getXYFromLatLonAlt(latLng.lat(), latLng.lng(), 0);

    return {
      lat: latLng.lat(),
      lon: latLng.lng(),
      x: pointXY[0],
      y: pointXY[1],
    };
  }

  getScreenCoords(lat: number, lon: number) {
    if (!this.map?.googleMap) {
      throw new Error("Map is not available.");
    }

    const mapDiv = this.map.googleMap.getDiv();
    const mapElement = mapDiv as HTMLElement;
    const bounds = mapDiv.getBoundingClientRect();

    const projection = this.map.googleMap.getProjection();
    const mapBounds = this.map.googleMap.getBounds();

    if (!projection || !mapBounds) {
      throw new Error("Projection or map bounds are not available.");
    }

    const latLng = new google.maps.LatLng(lat, lon);
    const worldPoint = projection.fromLatLngToPoint(latLng);

    if (!worldPoint) {
      throw new Error("Could not convert lat/lng to world point.");
    }

    const topRight = projection.fromLatLngToPoint(mapBounds.getNorthEast());
    const bottomLeft = projection.fromLatLngToPoint(mapBounds.getSouthWest());

    if (!topRight || !bottomLeft) {
      throw new Error("Could not get map bounds coordinates.");
    }

    const worldWidth = Math.abs(topRight.x - bottomLeft.x);
    const worldHeight = Math.abs(topRight.y - bottomLeft.y);

    const pixelX = ((worldPoint.x - bottomLeft.x) / worldWidth) * mapElement.offsetWidth;
    const pixelY = ((worldPoint.y - topRight.y) / worldHeight) * mapElement.offsetHeight;

    const clientX = pixelX + bounds.left;
    const clientY = pixelY + bounds.top;

    return { clientX, clientY };
  }

  rotationImage(angleDegrees: number): void {
    const current = this.editedImage() ?? this.modelData;
    if (!current) return;

    const updated = {
      ...current,
      imageTransform: { ...(current.imageTransform ?? {}), angle: angleDegrees }
    };

    if (this.editedImage()) this.editedImage.set(updated);
    else this.modelData = updated;

    this.applyImageTransform(updated);
  }

  applyImageTransform(model: Model): void {
    const activeImage = model;
    if (!activeImage?.mapImageSrc) return;

    const angle = this.getAngle(activeImage);
    const masks = this.getMasks(activeImage);
    const opacity = this.getOpacity(activeImage);

    if (!angle && !masks.length) {
      this.updateMap(activeImage);
      return;
    }

    const image = new Image();
    image.crossOrigin = 'anonymous';

    image.onload = () => {
      // 1. Create a canvas
      const sourceCanvas = document.createElement('canvas');
      sourceCanvas.width = image.width;
      sourceCanvas.height = image.height;
      const sourceCtx = sourceCanvas.getContext('2d')!;
      sourceCtx.drawImage(image, 0, 0);

      // 2. Masks
      if (masks.length) {
        const imageData = sourceCtx.getImageData(0, 0, image.width, image.height);
        ImageMaskEditor.applyMasksToImageData(imageData, masks, image.width, image.height);
        sourceCtx.putImageData(imageData, 0, 0);
      }

      // 3. Rotation
      if (angle) {
        const angleRad = angle * Math.PI / 180;
        const rotatedWidth = Math.abs(image.width * Math.cos(angleRad)) + Math.abs(image.height * Math.sin(angleRad));
        const rotatedHeight = Math.abs(image.width * Math.sin(angleRad)) + Math.abs(image.height * Math.cos(angleRad));

        const rotatedCanvas = document.createElement('canvas');
        rotatedCanvas.width = rotatedWidth;
        rotatedCanvas.height = rotatedHeight;
        const rotatedCtx = rotatedCanvas.getContext('2d')!;

        rotatedCtx.translate(rotatedWidth / 2, rotatedHeight / 2);
        rotatedCtx.rotate(angleRad);
        rotatedCtx.drawImage(sourceCanvas, -image.width / 2, -image.height / 2);
        rotatedCtx.resetTransform();

        var finalDataUrl = rotatedCanvas.toDataURL();
      } else {
        var finalDataUrl = sourceCanvas.toDataURL();
      }

      const updatedImage = {
        ...activeImage,
        mapImage: finalDataUrl,
        imageTransform: { ...activeImage.imageTransform, angle, opacity, masks }
      };

      this.imageAngle = angle;

      if (this.editedImage()) this.editedImage.set(updatedImage);
      else this.modelData = updatedImage;

      this.updateMap(updatedImage);
    };

    image.onerror = (err) => console.error('applyImageTransform error:', err);
    image.src = activeImage.mapImageSrc;
  }

  // ========================================
  // CAMERA MODAL
  // ========================================

  cameraBtn() {
    if (this.showLastAttachment) this.showLastAttachment = false
    else this.showLastAttachment = true;
  }

  onImageClick(index: number, attachments: Attachment[]) {
    let attachment = attachments[index];
    // TODO: Uncomment when GalleryComponent is available
    // const dialogRef = this.dialog.open(GalleryComponent, {
    //   width: "auto",
    //   height: "auto",
    //   data: {
    //     index,
    //     urls: attachments.map(a => a.url),
    //     startDate: attachment.createdDate,
    //     endDate: addMinutes(new Date(attachment.createdDate), 1).toISOString(),
    //     boxes: attachment.predictions,
    //     tagDisplayConfig: this.tagDisplayConfig
    //   }
    // });

    // dialogRef.afterClosed().subscribe((res) => {
    //   if (res == 'true') {

    //   }
    // });

  }

  getListAttachments() {
    this.attachmentService.getAttachments({ limit: 1 }).pipe(takeUntil(this.destroy$)).subscribe((attachments: any) => {
      this.parseAttachments(attachments);
    })
  }

  parseAttachments(attachments: any) {
    if (attachments && attachments.length) {
      // this.pAttachments = attachments.filter(t => t.predictions.length != 0) << non necessaire
      this.pAttachments = this.removeDuplicates(attachments, "tracker");// Removal of duplicates in the select viewv

      this.lastAttachment = this.pAttachments[this.pAttachments.length - 1] || null;
      this.lastPredictions = this.lastAttachment?.predictions || []; // this caused was causing error without '|| []'
      this.lastPredictions.forEach((t: any) => t.probability = t.probability.toFixed(2) * 100);
      this.pAttachments.forEach(p => {
        this.trackers.forEach(t => {
          if (p.tracker == t.id) p.trackerName = t.name;
        })
        if (!p.trackerName) p.trackerName = "N/A";
      })
    }
  }

  removeDuplicates(originalArray: any[], prop: string) {
    var newArray: any[] = [];
    var lookupObject: Record<string, any> = {};

    for (var i in originalArray) {
      lookupObject[originalArray[i][prop]] = originalArray[i];
    }

    for (i in lookupObject) {
      newArray.push(lookupObject[i]);
    }
    return newArray;
  }

  loadPredictions(trackerId: string) {
    const n = this.pAttachments.filter(p => p.tracker == trackerId);
    this.lastAttachment = n[n.length - 1] || null;
    this.lastPredictions = this.lastAttachment?.predictions || [];
  }

  getTrackerId(tracker: string | Tracker): string {
    if (typeof tracker === 'string') {
      return tracker;
    }
    return tracker?.id || '';
  }

  // ========================================
  // PLAYER
  // ========================================
  onPlayerChanged(data: any) {
    this.playMode = data.playMode;
    this.startDate = data.startDate;
    this.endDate = data.endDate;
    this.showTimeline = data.showTimeline;
    this.liveState = data.playMode == "replay" ? false : data.liveState;

    if (this.liveState) {
      if (this.virtualUnits && this.virtualUnits.length > 0) {
        this.sendVirtualTracks();
      }
    }
  }

  onTrackerPositionsChanged(tracks: Track[]) {
    const replayTracks = this.replayTrackers.length ?
      tracks.filter((t: Track) => this.replayTrackers.includes(typeof t.tracker === 'string' ? t.tracker : t.tracker?.id || '')) : [];
    if (!this.centered && tracks && tracks.length && replayTracks.length) {
      this.centerBtn(replayTracks[0]?.lat, replayTracks[0]?.lon, 18);
      this.centered = true;
    }

    if (tracks && tracks.length > 0) {
      tracks.forEach(async (track: Track) => {
        this.updateMapTrack(track);
        // this.setDebugMarker(format(track.createdDate, "mm:ss"), track.lat, track.lon);
      });
    }
  }

  updateMapTrack(track: any) {
    try {
      if (!track || !track.tracker) return;

      const trackerId = track.tracker.id || track.tracker;
      const trackerName = track.tracker.name || track.tracker;

      if ((!track.lat && !track.lon) && (track.x !== undefined && track.y !== undefined)) {
        let vec3 = new THREE.Vector3(track.x, track.y, 0);

        if (this.mapAngle) {
          var radians = this.mapAngle * (Math.PI / 180);
          vec3.applyAxisAngle(new THREE.Vector3(0, 0, 1), radians);
        }
        let lonLat = Tool3D.getLatLonFromXYZ2((this.projectXY[0] || 0) + vec3.x, (this.projectXY[1] || 0) + vec3.y, this.altitude);
        track.lon = lonLat[0];
        track.lat = lonLat[1];
      }

      if (this.tabletTrackerIds.has(trackerId)) {
        this.updateTabletMarker(trackerId, trackerName, track);
        return;
      }

      if (!this.markers || !this.markers[trackerId]) {
        // console.warn(`Marker not found for tracker ${trackerId}`);
        return;
      }

      let {
        marker,
        color,
        trace,
        unit,
        collider
      } = this.markers[trackerId];

      if (!marker) {
        console.warn(`Marker object is null for tracker ${trackerId}`);
        return;
      }

      if (!marker.map && this.replayUnitNames.length && this.centered) {
        Object.keys(this.markers).forEach(tracker => {
          if (this.markers[tracker]?.marker) {
            this.markers[tracker].marker.setMap(this.map.googleMap)
          }
        })
      }

      this.markers[trackerId].lastTrack = track;
      let latlon = new google.maps.LatLng(track.lat, track.lon);
      let pos = new google.maps.LatLng(track.lat, track.lon);
      marker.setPosition(latlon);

      if (this.clickedMarkerInfo && track.tracker.name == this.clickedMarkerInfo.name) {
        this.clickedMarkerInfo = {
          ...this.clickedMarkerInfo,
          lastTrack: track
        }
      }

      if (!trace) {
        trace = new google.maps.Polyline({
          path: [pos],
          strokeColor: color || '#000000',
          strokeWeight: 2,
          geodesic: true,
          strokeOpacity: 0.8
        });

        trace.setMap(this.map.googleMap);
      }

      let path = trace.getPath();
      let index = this.trackerObservableArray.findIndex(x => x.id == trackerId);
      this.loadedTraces.push({ trace, tracker: this.trackerObservableArray[index], marker: this.markers[trackerId].marker });

      if (index >= 0 && this.trackerObservableArray[index].trace === true) {
        if (trace.map == null) {
          trace.setMap(this.map.googleMap);
        } else {
          path.push(pos)
        }
      } else {
        path.pop();
      }

      if (index >= 0 && this.trackerObservableArray[index].showCollider === true && collider) {
        let child = null;
        let parent: any = this.getParentUnit(trackerId);

        if (unit.collider.geometry == "Crane" && (unit.crane.jibTracker || unit.crane.hookTracker)) {
          child = this.markers[unit.crane.jibTracker || unit.crane.hookTracker];
        }

        if (child) {
          collider.updateLatLonRotation(track, child.lastTrack)
        } else {
          collider.updateLatLonPosition(track)
        }

        if (parent) {
          parent.collider.updateLatLonRotation(parent.lastTrack, track)
        }
      }

      this.markers[trackerId].trace = trace; // Update trace
      return "ok";
    } catch (err) {
      console.error(`Error updating track for ${track?.tracker?.id || 'unknown'}:`, err);
      return null;
    }
  }

  // Convert an image URL to a canvas data URL
  private imageToCanvasDataUrl(src: string): Promise<string> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;

        const ctx = canvas.getContext('2d');
        if (!ctx) return reject(new Error('Canvas 2D context unavailable'));

        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };

      img.onerror = reject;
      img.src = src;
    });
  }

  // Ensure that the map image is in canvas data URL format
  private async ensureMapImageCanvas(): Promise<void> {
    if (!this.modelData) return;

    // Déjà en data URL => ok
    if (this.modelData.mapImage?.startsWith('data:image/')) return;

    const src = this.modelData.mapImage || this.modelData.mapImageSrc;
    if (!src) return;

    try {
      this.modelData.mapImage = await this.imageToCanvasDataUrl(src);
    } catch (e) {
      console.warn('ensureMapImageCanvas failed, fallback URL:', e);
    }
  }

  // Listens to router events to determine if the viewer maps page is active
  private bindRouterVisibilityEvents(): void {
    if (this.routerEventsBound) return;
    this.routerEventsBound = true;

    this.router.events
      .pipe(
        filter((e): e is NavigationStart | NavigationEnd =>
          e instanceof NavigationStart || e instanceof NavigationEnd
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        const url = event instanceof NavigationEnd ? event.urlAfterRedirects : event.url;
        const goingToViewerMaps = this.isViewerMapsUrl(url);

        // exit from maps -> stop
        if (!goingToViewerMaps && this.isViewerMapsActive) {
          this.isViewerMapsActive = false;
          this.stopVirtualTrackInterval();
        }

        // entering maps -> start
        if (goingToViewerMaps && !this.isViewerMapsActive) {
          this.isViewerMapsActive = true;
          this.startVirtualTrackInterval();
        }
      });
  }

  // Checks if a given URL corresponds to the viewer maps page
  private isViewerMapsUrl(url: string): boolean {
    return /\/project\/viewer\/maps(\?|$)/.test(url) || /\/viewer-maps(\?|$)/.test(url);
  }

  // Sends virtual tracks for all virtual units
  private startVirtualTrackInterval(): void {
    if (this.trackInterval) return;
    if (!this.hasStartedSendingTracks) return;

    this.trackInterval = setInterval(() => {
      if (this.liveState === true && this.virtualUnitsInfo.length > 0) {
        this.sendVirtualTracks();
      }
    }, 5000);
  }

  // Clears the interval responsible for sending virtual tracks
  private stopVirtualTrackInterval(): void {
    if (!this.trackInterval) return;
    clearInterval(this.trackInterval);
    this.trackInterval = null;
  }

  resetVariables(): void {
    this.units = [];
    this.trackers = [];
    this.markers = {};
    this.loadedMarkers = [];
    this.loadedTraces = [];
    this.trackerObservableArray = [];
    this.isInitialized = false;
    this.centered = false;
    this.hasStartedSendingTracks = false;
    this.virtualUnitsInfo = [];
    this.unitPoints = [];
    this.modelData = null;
    this.showMap = true;

    if (this.trackInterval) {
      clearInterval(this.trackInterval);
      this.trackInterval = null;
    }
  }

  getColorHex(colorValue: string): string {
    return this.colors[colorValue] || this.colors['default'] || '#000000';
  }

  getSelectedColorValue(): string {
    return this.zoneForm.get('color')?.value || '';
  }

  getSelectedColorTitle(): string {
    const colorValue = this.zoneForm.get('color')?.value;
    const selectedColor = this.zoneColors().find(c => c.value === colorValue);
    return selectedColor?.title || '';
  }

  onImageOpacityChanged(opacity: number): void {
    this.currentImageOpacity = opacity;

    if (this.groundOverlay) {
      this.groundOverlay.setOpacity(opacity / 100);
    }

    const current = this.editedImage();
    if (!current || this.getOpacity(current) === opacity / 100) return;

    this.editedImage.set({
      ...current,
      imageTransform: {
        ...(current.imageTransform ?? {}),
        opacity: opacity / 100
      }
    });
  }

  onImageOpacityCommit(opacity: number): void {
    const current = this.editedImage();
    if (!current || this.getOpacity(current) === opacity / 100) return;

    this.editedImage.set({
      ...current,
      imageTransform: {
        ...(current.imageTransform ?? {}),
        opacity: opacity / 100
      }
    });
  }

  public getAngle(model: Model): number {
    return model.imageTransform?.angle ?? model.mapImageAngle ?? 0;
  }

  public getOpacity(model: Model): number {
    return model.imageTransform?.opacity ?? model.opacity ?? 1;
  }

  public getMasks(model: Model): ImageMask[] {
    return model.imageTransform?.masks ?? [];
  }
}
